import { Box, Chip, Slider, Switch, Tooltip, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConstraintWarningIcon } from './constraintParts';
import { labelSx, sliderSwitchSx } from './constraintStyles';
import { IntensityThresholdViewModel } from './useIntensityThreshold';

/**
 * Intensity-threshold constraint: deposit contour only on voxels within an
 * intensity range (HU for CT, SUVbw for PET). Presentational — the view-model
 * ({@link useIntensityThreshold}) supplies state, unit handling, and handlers.
 */
export default function IntensityThresholdSection({
    available,
    enabled,
    labelTooltipId,
    unavailableTooltipId,
    hasRange,
    displayMin,
    displayMax,
    sliderValue,
    sliderStep,
    unitLabel,
    formatValue,
    presets,
    activePresetIndex,
    onToggle,
    onRangeChange,
    onApplyPreset,
}: IntensityThresholdViewModel) {
    const intl = useIntl();
    const dimmedSx = { opacity: available ? 1 : 0.45 };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 0,
                    }}
                >
                    <Tooltip
                        title={intl.formatMessage({ id: labelTooltipId })}
                        placement="left"
                    >
                        {/* Dim the label so the whole row reads as inactive
                            when thresholding can't apply. */}
                        <Typography sx={{ ...labelSx, ...dimmedSx }}>
                            <FormattedMessage id="thresholdConstraintLabel" />
                        </Typography>
                    </Tooltip>
                    {!available && (
                        <ConstraintWarningIcon
                            titleId={unavailableTooltipId}
                            placement="right"
                        />
                    )}
                </Box>
                <Switch
                    disableRipple
                    sx={{ ...sliderSwitchSx, ...dimmedSx }}
                    disabled={!available}
                    checked={enabled && available}
                    onChange={(_, checked) => onToggle(checked)}
                />
            </Box>
            {available && enabled && hasRange && (
                <Box sx={{ mt: 1, px: 0.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.25,
                        }}
                    >
                        <Typography sx={labelSx}>
                            {formatValue(sliderValue[0])}
                            {' – '}
                            {formatValue(sliderValue[1])} {unitLabel}
                        </Typography>
                    </Box>
                    <Slider
                        size="small"
                        value={sliderValue}
                        min={displayMin}
                        max={displayMax}
                        step={sliderStep}
                        disableSwap
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) =>
                            `${formatValue(v)} ${unitLabel}`
                        }
                        onChange={(_, value) => onRangeChange(value)}
                        aria-label={intl.formatMessage({
                            id: 'thresholdRangeAria',
                        })}
                        sx={{ py: 0.5 }}
                    />
                    {presets.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mt: 0.25,
                            }}
                        >
                            {presets.map((preset, idx) => {
                                // Highlight the chip whose range matches the
                                // current selection so the active preset is
                                // obvious at a glance.
                                const active = idx === activePresetIndex;
                                return (
                                    <Chip
                                        key={preset.labelId}
                                        size="small"
                                        label={intl.formatMessage({
                                            id: preset.labelId,
                                        })}
                                        onClick={() => onApplyPreset(idx)}
                                        sx={{
                                            height: 20,
                                            fontSize: 11,
                                            color: active
                                                ? '#fff'
                                                : 'rgba(255, 255, 255, 0.85)',
                                            backgroundColor: active
                                                ? 'primary.main'
                                                : 'rgba(255, 255, 255, 0.08)',
                                            '&:hover': {
                                                backgroundColor: active
                                                    ? 'primary.main'
                                                    : 'rgba(255, 255, 255, 0.16)',
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
}
